import React, { useState } from 'react';
import { Avatar, ActionIcon, Indicator, Menu, Popover, UnstyledButton, Box } from '@mantine/core';
import { ThemedText } from '../Typography';
import { Bell } from 'tabler-icons-react';
import { IconLayoutDashboard, IconLogout } from '@tabler/icons-react';
import { Link, router } from '@inertiajs/react';
import { showNotification } from '@mantine/notifications';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import { themeAlpha, whiteTextGradient } from '../../src/utils/pageTheme';
import { COLORS } from '../../src/constants/theme';
import '../ui/Buttons/ActionIconButton.css';
import ThemedButton from '../ui/Buttons/ThemedButton';

// Use the User type from your UserContext instead of defining our own
interface UserSectionProps {
  user: any; // Using any to match your UserContext type - you can replace with your actual User interface
  onLogout?: () => void;
}

const UserSection: React.FC<UserSectionProps> = ({ user, onLogout }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [desktopMenuOpened, setDesktopMenuOpened] = useState(false);

  // Use notifications hook (polls every 30 seconds)
  // Only run if user is logged in
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications(
    user ? 30000 : 0,
  );

  const getLoginUrl = () => {
    const currentPath = window.location.pathname;
    return `/login?return_to=${encodeURIComponent(currentPath)}`;
  };

  const handleCloseDropdown = () => {
    setShowDropdown(false);
  };

  const getDefaultAvatarUrl = () => {
    return 'https://dancewise.s3.amazonaws.com/WCWD+blank+profile2.png';
  };

  const handleLogout = async () => {
    if (onLogout) {
      await onLogout();
    }
    setMobileMenuOpened(false);
    setDesktopMenuOpened(false);
  };

  const handleDashboardClick = () => {
    router.visit(`/users/${user.username}/user-settings`);
    setMobileMenuOpened(false);
    setDesktopMenuOpened(false);
  };

  if (!user) {
    return (
      <div
        className="auth-buttons-container"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <Link
          href={getLoginUrl()}
          className="login-link"
          style={{
            color: '#f2f2f2',
            textDecoration: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
            padding: '8px 16px',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          Log in
        </Link>
        <Link href="/register" style={{ textDecoration: 'none' }}>
          <ThemedButton
            variant="filled"
            color="#EA580C"
            size="md"
            className="signup-button"
            style={{
              // White text on a fill deep enough for it, on every theme (#2344).
              background: whiteTextGradient(),
              color: COLORS.textOnAccent,
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.95rem',
              padding: '8px 20px',
              boxShadow: `0 2px 8px ${themeAlpha(0.3)}`,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${themeAlpha(0.4)}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 2px 8px ${themeAlpha(0.3)}`;
            }}
          >
            Join the Dance
          </ThemedButton>
        </Link>
      </div>
    );
  }

  return (
    <div className="navy-left" style={{ position: 'relative', marginLeft: 'auto' }}>
      {/* Notification Bell + Dropdown (Popover handles positioning, click-outside, escape) */}
      <Popover
        opened={showDropdown}
        onChange={setShowDropdown}
        position="bottom-end"
        offset={8}
        withinPortal
        zIndex={10000}
        closeOnClickOutside
        closeOnEscape
        styles={{
          dropdown: {
            background: 'transparent',
            border: 'none',
            padding: 0,
          },
        }}
      >
        <Popover.Target>
          <div className="notification-bell-container">
            <Indicator
              size={unreadCount > 0 ? 16 : 0}
              color="#EA580C"
              processing={unreadCount > 0}
              label={unreadCount > 9 ? '9+' : unreadCount}
              offset={3}
              disabled={unreadCount === 0}
            >
              <div className="notification-bell-motion">
                <ActionIcon
                  size="lg"
                  variant="transparent"
                  onClick={() => setShowDropdown((s) => !s)}
                  aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                  // 44px tap zone around the 34px bell (#2344).
                  className="notification-bell action-icon-tap"
                >
                  <Bell strokeWidth={1.75} color="gray" style={{ width: '30px', height: '30px' }} />
                </ActionIcon>
              </div>
            </Indicator>
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <NotificationDropdown
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={markAsRead}
            onMarkAllAsRead={markAllAsRead}
            onDelete={deleteNotification}
            onClose={handleCloseDropdown}
          />
        </Popover.Dropdown>
      </Popover>

      {/* Mobile Avatar with Menu */}
      <div className="mobile-avatar">
        <Menu
          width={200}
          position="bottom-end"
          shadow="xl"
          opened={mobileMenuOpened}
          onChange={setMobileMenuOpened}
          zIndex={2000}
          styles={{
            dropdown: {
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              boxShadow: '0 0 25px rgba(0, 0, 0, 0.5), 0 8px 24px rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <Menu.Target>
            <Box
              component="div"
              p="2px"
              style={{
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--theme-accent-hover, #C2410C) 0%, var(--theme-accent-deep, #9E3D08) 50%, var(--theme-accent-deeper, #783006) 100%)',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 15px color-mix(in srgb, var(--theme-accent-hover, #c2410c) 60%, transparent)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <UnstyledButton
                onClick={() => setMobileMenuOpened(!mobileMenuOpened)}
                aria-label="Account menu"
                // 44px tap zone around the 38px avatar (#2344).
                className="action-icon-tap"
                variant="subtle"
                style={{
                  cursor: 'pointer',
                  padding: 0,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
                  border: 'none',
                  display: 'block',
                  width: '100%',
                  height: '100%',
                }}
              >
                <Avatar src={user.avatar_url || getDefaultAvatarUrl()} style={{ backgroundColor: 'transparent' }} />
              </UnstyledButton>
            </Box>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconLayoutDashboard size={18} color="#f2f2f2" />}
              onClick={handleDashboardClick}
              style={{
                fontSize: '0.875rem',
                color: '#f2f2f2',
              }}
              styles={{
                item: {
                  padding: '16px 20px', // Larger padding for mobile (48px+ height)
                  minHeight: '48px', // Ensure minimum touch target size
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                },
              }}
            >
              <ThemedText size="sm" className="poppins">
                Dashboard
              </ThemedText>
            </Menu.Item>
            <Menu.Item
              leftSection={<IconLogout size={18} color="#f2f2f2" />}
              onClick={handleLogout}
              style={{
                fontSize: '0.875rem',
                color: '#f2f2f2',
              }}
              styles={{
                item: {
                  padding: '16px 20px', // Larger padding for mobile (48px+ height)
                  minHeight: '48px', // Ensure minimum touch target size
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                },
              }}
            >
              <ThemedText size="sm" className="poppins">
                Logout
              </ThemedText>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>

      {/* Desktop User Profile with Menu */}
      <div className="user-profile">
        <Menu
          width={200}
          position="bottom-end"
          shadow="xl"
          opened={desktopMenuOpened}
          onChange={setDesktopMenuOpened}
          zIndex={2000}
          styles={{
            dropdown: {
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              boxShadow: '0 0 25px rgba(0, 0, 0, 0.5), 0 8px 24px rgba(0, 0, 0, 0.7)',
            },
          }}
        >
          <Menu.Target>
            <Box
              component="div"
              p="2px"
              style={{
                borderRadius: '20px',
                background: 'linear-gradient(135deg, var(--theme-accent-hover, #C2410C) 0%, var(--theme-accent-deep, #9E3D08) 50%, var(--theme-accent-deeper, #783006) 100%)',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 15px color-mix(in srgb, var(--theme-accent-hover, #c2410c) 40%, transparent)';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Box
                style={{
                  background: 'linear-gradient(135deg, var(--theme-accent-hover, #C2410C) 0%, var(--theme-accent-deep, #9E3D08) 50%, var(--theme-accent-deeper, #783006) 100%)',
                  borderRadius: '18px',
                  padding: '4px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <UnstyledButton
                  onClick={() => setDesktopMenuOpened(!desktopMenuOpened)}
                  variant="subtle"
                  style={{
                    cursor: 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '8px',
                    textDecoration: 'none',
                    color: '#f2f2f2',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} className="avatar_small" style={{ marginRight: 8 }} alt="Avatar" />
                  ) : (
                    <img src={getDefaultAvatarUrl()} className="avatar_small" style={{ marginRight: 8 }} alt="Default avatar" />
                  )}
                  <ThemedText className="username-nav poppins" fw={600} size="sm">
                    {user.username}
                  </ThemedText>
                </UnstyledButton>
              </Box>
            </Box>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconLayoutDashboard size={18} color="#f2f2f2" />}
              onClick={handleDashboardClick}
              style={{
                fontSize: '0.875rem',
                color: '#f2f2f2',
              }}
              styles={{
                item: {
                  padding: '12px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                },
              }}
            >
              <ThemedText size="xs" className="poppins">
                Dashboard
              </ThemedText>
            </Menu.Item>
            <Menu.Item
              leftSection={<IconLogout size={18} color="#f2f2f2" />}
              onClick={handleLogout}
              style={{
                fontSize: '0.875rem',
                color: '#f2f2f2',
              }}
              styles={{
                item: {
                  padding: '12px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  },
                },
              }}
            >
              <ThemedText size="xs" className="poppins">
                Logout
              </ThemedText>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    </div>
  );
};

export default UserSection;
